import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { FormField, Input, Select } from '../ui/Form';
import { Button } from '../ui/Button';
import { useAccountingStore } from '../../stores/accountingStore';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../../stores/businessUnitStore';
import type { Account } from '../../types/accounting';

interface AccountEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  account?: Account | null;
  onSave: (data: Partial<Account>) => Promise<void>;
}

const initialFormData: Partial<Account> = {
  code: '',
  name: '',
  type: 'asset',
  isAnalytic: false,
  allowReconciliation: false,
  description: ''
};

export function AccountEditModal({ isOpen, onClose, account, onSave }: AccountEditModalProps) {
  const { mode } = useAccountingStore();
  const { currentFiscalYear } = useFiscalYearStore();
  const { selectedBusinessUnit } = useBusinessUnitStore();
  
  const [formData, setFormData] = useState<Partial<Account>>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Réinitialiser le formulaire quand le modal s'ouvre ou que le compte change
  useEffect(() => {
    if (isOpen) {
      if (account) {
        setFormData({
          code: account.code || '',
          name: account.name || '',
          type: account.type || 'asset',
          isAnalytic: account.isAnalytic || false,
          allowReconciliation: account.allowReconciliation || false,
          description: account.description || '',
          standard: account.standard || mode
        });
      } else {
        setFormData({
          ...initialFormData,
          standard: mode
        });
      }
      setError(null);
    }
  }, [isOpen, account, mode]);

  const handleInputChange = (field: keyof Account, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.code?.trim()) {
      setError('Le code du compte est requis');
      return false;
    }
    if (!formData.name?.trim()) {
      setError('Le libellé du compte est requis');
      return false;
    }
    if (!formData.type) {
      setError('Le type de compte est requis');
      return false;
    }
    // Validation du format du code (6 chiffres pour SYSCOHADA)
    if (!/^\d{3,6}$/.test(formData.code.trim())) {
      setError('Le code doit être composé de 3 à 6 chiffres');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Préparer les données avec les champs requis par l'API
      const accountData: Partial<Account> & { companyId?: string; fiscalYearId?: string } = {
        ...formData,
        code: formData.code?.trim(),
        name: formData.name?.trim(),
        standard: mode,
        // Ajouter les champs requis par l'API
        fiscalYearId: currentFiscalYear?.id,
        companyId: selectedBusinessUnit?.id || 'default-company',
        businessUnitId: selectedBusinessUnit?.id,
        businessUnitType: selectedBusinessUnit?.type
      };

      await onSave(accountData);
      // Le modal sera fermé par le parent après succès
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du compte:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde du compte');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData(initialFormData);
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={account ? 'Modifier le compte' : 'Nouveau compte'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Code comptable" required>
            <Input
              value={formData.code || ''}
              onChange={(e) => handleInputChange('code', e.target.value)}
              placeholder="411000"
              maxLength={6}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Code SYSCOHADA (3-6 chiffres)
            </p>
          </FormField>

          <FormField label="Type de compte" required>
            <Select
              value={formData.type || 'asset'}
              onChange={(e) => handleInputChange('type', e.target.value)}
              disabled={loading}
            >
              <option value="asset">Actif</option>
              <option value="liability">Passif</option>
              <option value="equity">Capitaux propres</option>
              <option value="revenue">Produits</option>
              <option value="expense">Charges</option>
            </Select>
          </FormField>
        </div>

        <FormField label="Libellé du compte" required>
          <Input
            value={formData.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Clients"
            disabled={loading}
          />
        </FormField>

        <FormField label="Description">
          <Input
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Description optionnelle du compte"
            disabled={loading}
          />
        </FormField>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isAnalytic"
            checked={formData.isAnalytic || false}
            onChange={(e) => handleInputChange('isAnalytic', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="isAnalytic" className="text-sm text-gray-700 dark:text-gray-300">
            Compte analytique
          </label>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="allowReconciliation"
            checked={formData.allowReconciliation || false}
            onChange={(e) => handleInputChange('allowReconciliation', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="allowReconciliation" className="text-sm text-gray-700 dark:text-gray-300">
            Autoriser le lettrage
          </label>
          <span className="text-xs text-muted-foreground">(comptes tiers : clients, fournisseurs, personnel)</span>
        </div>

        {/* Info sur l'exercice fiscal */}
        {currentFiscalYear && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-2 rounded-lg text-sm text-blue-700 dark:text-blue-300">
            Ce compte sera créé pour l'exercice : <strong>{currentFiscalYear.code}</strong>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={loading}
            isLoading={loading}
          >
            {account ? 'Modifier' : 'Créer le compte'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}